// A 45-byte question, asked on a timer: "has the stored data changed?"
//
// The dashboard fetches its rows once and holds them for staleTime -- twelve
// hours -- so an admin rebuild left every OTHER open tab showing pre-rebuild
// numbers with nothing on screen to say so. The only browser that noticed was the
// one that pressed the button, because it watched the job finish and reset
// itself. Everyone else had to know to reload, which meant no number could be
// trusted without a procedure.
//
// The answer is one short string and it REPLACES the previous one, so nothing
// accumulates. Against a 4.4 MB data load it is about a hundred-thousandth of one
// fetch.
//
// The stamp is then part of the data requests' own cache key and URL, rather than
// something that triggers an invalidation. That distinction matters: invalidating
// would refetch the same URL, which the browser is entitled to answer from its own
// five-minute cache -- returning exactly the stale rows this exists to replace.
// Changing the URL leaves nothing able to serve the old answer.
//
// Every caller shares one query key, so this is a single request no matter how
// many hooks read it.

import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

const CHECK_EVERY_MS = 60 * 1000;

export interface DataStampResponse {
  lastUpdated: string | null;
  dataStamp?: string;
}

export function useDataStampQuery() {
  return useQuery({
    queryKey: ["scheduling-data-stamp"],
    queryFn: () => api<DataStampResponse>("/api/scheduling/last-updated"),
    refetchInterval: CHECK_EVERY_MS,
    // Keeps checking while the tab is in the BACKGROUND, which TanStack does not
    // do by default. The background tab is the case this exists for: the
    // dashboard sitting behind the admin page while a rebuild runs. Without this
    // you would switch to it, see the old numbers, and watch them change under you
    // a moment later -- which is the confusion this is meant to end, not a fix for
    // it. At 45 bytes a minute the whole day costs 65 KB.
    refetchIntervalInBackground: true,
    // And immediately on focus, for a tab left open overnight where a minute of
    // staleness is still a minute too long to be looking at it.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

/**
 * The current data stamp, and whether it has arrived yet.
 *
 * `ready` matters more than it looks. The stamp is part of the data queries' key
 * and URL, so firing them before it arrives fetches everything under a placeholder
 * and then AGAIN under the real stamp -- measured as two full data loads per page
 * open, which would have doubled the cost of the thing this sits in front of. The
 * data queries wait for `ready` instead; it costs one 45-byte round trip.
 */
export function useDataStamp(): { stamp: string; ready: boolean } {
  const { data, isError } = useDataStampQuery();

  // ANY answer makes us ready, including one with no stamp in it.
  //
  // This function gates whether the dashboard may fetch its rows at all, so
  // anything it fails to account for is a dashboard that never loads. It
  // previously waited for the FIELD rather than the response, which meant a
  // perfectly successful reply that happened not to carry a stamp -- an older
  // server still serving, a proxy holding the previous cached response, any
  // moment where the front end is ahead of the back end -- left it waiting
  // forever, with no error and no timeout. That took the dashboard down.
  //
  // Losing rebuild detection is a small, quiet loss. Losing the dashboard is not
  // a tradeoff worth making for it, so this always fails open.
  //
  // A stamp already held also SURVIVES a failed check: React Query keeps the last
  // successful data through an errored refetch, and dropping to "0" would change
  // the key twice for a momentary blip, reloading everything both times. A check
  // that fails told us nothing; it is not news that the data changed.
  if (data !== undefined) return { stamp: data.dataStamp ?? "0", ready: true };
  if (isError) return { stamp: "0", ready: true };
  return { stamp: "0", ready: false };
}
